// Bridge-or-localStorage backing for a Supermail setting that AMC has promoted to an AppSettings
// key. Inside AMC (the __AMC_SUPERMAIL__ bridge is present) the value is read/written through
// AppSettings, so it is CLI-controllable and synced live; standalone / in tests / on an older AMC
// (no bridge) the caller falls back to the vendored localStorage behavior, unchanged. Each setting
// store uses this to stay a thin wrapper with its existing public API.
type Bridge = NonNullable<Window['__AMC_SUPERMAIL__']>
type UiSettings = ReturnType<NonNullable<Bridge['getSupermailUiSettings']>>
type UiSettingKey = keyof UiSettings

function bridge(): Bridge | undefined {
  return typeof window !== 'undefined' ? window.__AMC_SUPERMAIL__ : undefined
}

/** True when AMC's promoted-settings bridge is available (installed AND this AMC build has the
 *  methods). Standalone / older AMC / tests without it → false, and callers use localStorage. */
export function hasAmcUiSettings(): boolean {
  const b = bridge()
  return !!(
    b &&
    b.getSupermailUiSettings &&
    b.setSupermailUiSetting &&
    b.onSupermailUiSettingsChange
  )
}

/** Read one promoted setting from AppSettings via the bridge, or undefined when unavailable. */
export function readAmcUiSetting<K extends UiSettingKey>(key: K): UiSettings[K] | undefined {
  const b = bridge()
  if (!b?.getSupermailUiSettings) return undefined
  try {
    return b.getSupermailUiSettings()[key]
  } catch {
    return undefined
  }
}

/** Write one promoted setting back to AppSettings via the bridge (no-op when unavailable). */
export function writeAmcUiSetting<K extends UiSettingKey>(key: K, value: UiSettings[K]): void {
  bridge()?.setSupermailUiSetting?.(key, value)
}

/** Subscribe to live changes (a CLI PATCH or the in-app toggle). The callback gets the whole
 *  snapshot; the store picks its key(s). Returns a no-op unsubscribe when no bridge. */
export function subscribeAmcUiSettings(cb: (settings: UiSettings) => void): () => void {
  const b = bridge()
  if (!b?.onSupermailUiSettingsChange) return () => {}
  return b.onSupermailUiSettingsChange(cb)
}
